// The app shell — a faithful port of mockup-v9 (the locked, known-good UI/UX): rail + main column
// (top bar + active surface) + phone bottom nav + Add FAB + avatar sheet. Pure string builders over
// the planner models; the render vocabulary (classes) mirrors the mock so style.css ports 1:1.
// Nothing is conveyed by colour alone: every type/owner/status carries an icon + text (REQ-A11Y-002).
import { escapeHtml } from "./html";
import { icon, iconLabel, type IconName } from "./icons";
import type { ViewItem } from "./view";
import type { AlertItem } from "./alerts";
import {
  isoWeek,
  isReadOnly,
  itemOwners,
  slippedLine,
  sourceLabel,
  termContext,
  timeOfDay,
  type CalMode,
  type DayGroup,
  type MonthModel,
  type TasksModel,
  type Term,
  type TodayModel,
  type WeekModel,
} from "./planner";
import { rowActions } from "./row";
import { recurrenceLabel } from "./recurrence";
import { sheetShell } from "./render";
import type { DigestModel, DigestPeriod } from "./digest";
import { initials, ownerMark } from "./owner";
export { initials };

// A recurring item wears a "series" chip so it's clear at a glance it repeats (REQ-CAL-011). The
// chip carries the icon + the human cadence ("every Thu") — never colour alone (REQ-A11Y-002).
function recurChip(it: ViewItem): string {
  const rl = recurrenceLabel(it.rrule as string | null | undefined);
  return rl ? `<span class="recur-chip">${icon("repeat", 13)}<span>${escapeHtml(rl)}</span></span>` : "";
}
// A compact secondary line for a row: its place and/or the recurring-series chip (REQ-CAL-011).
// Shared by event and to-do rows. Empty string when neither applies.
function rowSub(it: ViewItem): string {
  const bits: string[] = [];
  // The place is a real affordance (REQ-CAL-013↺): tapping it opens Maps AT that place. The sub-line
  // lives inside the whole-row open button (no nested <button> allowed), so it carries `data-map-loc`
  // and the row handler delegates a tap on it to Maps instead of opening the edit sheet.
  if (it.location) {
    const loc = String(it.location);
    bits.push(`<span class="sub-loc" data-map-loc="${escapeHtml(loc)}" role="link" tabindex="0" aria-label="Find ${escapeHtml(loc)} on the map">${icon("map-pin", 14)}${escapeHtml(loc)}</span>`);
  }
  const chip = recurChip(it);
  if (chip) bits.push(chip);
  return bits.length ? `<span class="it-sub">${bits.join("")}</span>` : "";
}

export type Tab = "today" | "calendar" | "tasks" | "trolley";
export type WeekLayout = "list" | "board";
export type { CalMode };

export interface SeeContext {
  activeTab: Tab;
  calMode?: CalMode;
  weekLayout?: WeekLayout;
  filter: "all" | "mine";
  me: { id: string; name: string };
  members: Array<{ id: string; name: string }>;
  ownerName: (id: string | null | undefined) => string;
  ownerColor?: (id: string | null | undefined) => string | null | undefined;
  role: string;
  theme: "light" | "dark";
  familyName: string;
  avatarOpen: boolean;
  dateLabel: string; // "Thu 13" (+ month in the sub) or "Tasks"/"Trolley"
  dateSub?: string; // the muted month suffix on the date header (mock .sub)
  trolleyStore: string;
  trolleyDone?: number; // count of recently-ticked groceries → the Undo affordance
  tasksDone?: number; // count of recently-ticked tasks/notes → the Tasks Undo affordance
  inbound?: number; // ingested captures awaiting review → the "Needs a look" banner (REQ-INGEST-004)
  alerts?: AlertItem[]; // the "Know" tier — dismissible heads-ups on Today (REQ-ALERT-002)
  digest?: DigestModel; // the live in-app digest for Today (REQ-DIGEST-001)
  todayCount?: number; // Today badge on the bottom nav
  todayDate?: string; // the real current local date (yyyy-mm-dd) → the filled Month "today" cell
  termCtx?: Term[] | null;
}

export interface ShellModels {
  today: TodayModel;
  week: WeekModel;
  month?: MonthModel;
  day?: DayGroup;
  tasks?: TasksModel;
  trolley?: ViewItem[];
}

// ── Rows (mock .item) — lean: no kebab, no "Synced" badge. Tapping the row opens the item sheet
// (where Reschedule / Delete / Complete live); the circle completes to-dos; events show a lead
// icon + right-aligned time. Owner is the shared avatar mark. Source (Elm Park / Howick) is a tag. ──
function rowOwner(it: ViewItem, ctx: SeeContext): string {
  const ids = itemOwners(it); // all assigned owners → a stacked mark (REQ-ITEM-006)
  return ids.length
    ? ownerMark(ids.map((id) => ({ id, name: ctx.ownerName(id), color: ctx.ownerColor?.(id) })))
    : "";
}
function sourceBadge(it: ViewItem): string {
  const label = sourceLabel(it.source);
  return label ? `<span class="src-badge">${escapeHtml(label)}</span>` : "";
}

function rowItem(it: ViewItem, ctx: SeeContext): string {
  const model = rowActions(it, { pointer: "touch" });
  const id = escapeHtml(it.id);
  const t = escapeHtml(it.title);
  const isEvent = it.type === "event";
  const time = isEvent && it.due_at ? timeOfDay(it.due_at) : "";
  // Preserve a captured duration by showing the range (REQ-CAL-012): "3:00–4:00 pm".
  const endTime = isEvent && it.end_at ? timeOfDay(it.end_at as string) : "";
  const timeLabel = time && endTime ? `${time}–${endTime}` : time;
  if (isEvent) {
    const lead = `<span class="lead" aria-hidden="true">${icon(time ? "clock" : "event", 20)}</span>`;
    return `<li class="item event"${isReadOnly(it.source) ? ' data-readonly="true"' : ""} data-id="${id}">
      ${lead}
      <button type="button" class="row-open" data-act="open" data-id="${id}" aria-label="Open event: ${t}"><span class="it-body"><span class="it-title">${t}</span>${rowSub(it)}</span>${sourceBadge(it)}</button>
      ${timeLabel ? `<span class="it-time">${escapeHtml(timeLabel)}</span>` : ""}
      ${rowOwner(it, ctx)}
    </li>`;
  }
  const circle = model.complete
    ? `<button type="button" class="row-circle" data-act="done" data-id="${id}" aria-label="Complete ${t}"><span class="circle" aria-hidden="true"></span></button>`
    : "";
  return `<li class="item todo" data-id="${id}">
    ${circle}
    <button type="button" class="row-open" data-act="open" data-id="${id}" aria-label="Open task: ${t}"><span class="it-body"><span class="it-title">${t}</span>${rowSub(it)}</span></button>
    ${rowOwner(it, ctx)}
  </li>`;
}

const panel = (rows: string, empty = "Nothing on today."): string =>
  `<ul class="panel items">${rows || `<li class="item"><span class="it-title" style="color:var(--color-text-muted)">${escapeHtml(empty)}</span></li>`}</ul>`;

// ── Today (mock today()) — "N things today", one panel, then the demoted Slipped line, then the
// compact "Next few days" list (REQ-VIEW-019). No "Today" heading: the date header carries it. ──
function slippedLineHTML(overdue: ViewItem[]): string {
  const s = slippedLine(overdue);
  if (!s) return "";
  return `<button type="button" class="slip-line" data-act="open-overdue" aria-label="${s.count} slipped, starting with ${escapeHtml(s.firstTitle)}. Open overdue tasks.">
    ${icon("warning", 18)}<span class="slip-text">${s.count} slipped · ${escapeHtml(s.firstTitle)}</span><span class="slip-caret" aria-hidden="true">${icon("caret-right", 16)}</span>
  </button>`;
}
// Tomorrow's items only (the Today peek shows just tomorrow now; the full week is one link away).
function tomorrowItems(m: TodayModel): ViewItem[] {
  const t = Date.parse(`${m.today}T00:00:00Z`);
  const day = m.nextDays.find((d) => Math.round((Date.parse(`${d.date}T00:00:00Z`) - t) / 86400000) === 1);
  return day ? day.items : [];
}
// A fully-clear Today (nothing due, nothing slipped, nothing coming up) gets a warm illustrative
// empty state rather than a bare panel (REQ-UX-003).
function emptyToday(ctx: SeeContext): string {
  const msg = ctx.role === "child" ? "Nothing on your list. Nice." : "Nothing on today. Enjoy the calm.";
  return `<div class="calm-empty">${icon("moon", 40)}<p>${escapeHtml(msg)}</p></div>`;
}
// ── Digest (feature 009) — a calm greeting header on Today that opens the full digest sheet. ──
export function digestGreeting(period: DigestPeriod): string {
  return period === "weekly" ? "The week ahead" : period === "evening" ? "This evening" : "Good morning";
}
function digestHeader(ctx: SeeContext): string {
  const d = ctx.digest;
  if (!d) return "";
  const bits: string[] = [];
  if (d.counts.today) bits.push(`${d.counts.today} today`);
  if (d.counts.slipped) bits.push(`${d.counts.slipped} slipped${d.counts.stale ? ` · ${d.counts.stale} stale` : ""}`);
  if (d.period === "weekly" && d.counts.upcoming) bits.push(`${d.counts.upcoming} this week`);
  const summary = bits.length ? bits.join(" · ") : "you're all clear";
  return `<button type="button" class="digest-head" data-act="open-digest" aria-label="Open your digest">
    <span class="dg-greet">${digestGreeting(d.period)}</span>
    <span class="dg-summary">${escapeHtml(summary)}</span>
    <span class="dg-caret" aria-hidden="true">${icon("caret-right", 16)}</span>
  </button>`;
}

/** The "Know" tier (REQ-ALERT-002): a dismissible strip of FYI heads-ups from ingested mail —
 *  charge/bill notices, service changes — read-and-dismiss, never a to-do. */
function headsUpStrip(alerts: AlertItem[] | undefined): string {
  if (!alerts || !alerts.length) return "";
  const cards = alerts
    .map(
      (a) => `<li class="hu-card">
      <span class="hu-icon" aria-hidden="true">${icon("warning", 18)}</span>
      <span class="hu-body"><span class="hu-title">${escapeHtml(a.title)}</span>${a.summary ? `<span class="hu-sub">${escapeHtml(a.summary)}</span>` : ""}${a.url ? `<a class="hu-open" href="${escapeHtml(a.url)}" target="_blank" rel="noopener noreferrer">${iconLabel("navigation", "Open")}</a>` : ""}</span>
      <span class="hu-actions">
        <button type="button" class="hu-add" data-act="alert-promote" data-id="${escapeHtml(a.id)}" aria-label="Turn into a task or event: ${escapeHtml(a.title)}" title="Make a task or event">${icon("plus", 16)}</button>
        <button type="button" class="hu-x" data-act="alert-dismiss" data-id="${escapeHtml(a.id)}" aria-label="Dismiss: ${escapeHtml(a.title)}">${icon("cancel", 16)}</button>
      </span>
    </li>`
    )
    .join("");
  const n = alerts.length;
  return `<section class="heads-up" aria-label="${n} heads-up${n === 1 ? "" : "s"}">
    <div class="hu-head">${icon("warning", 14)}<span>Heads-up</span></div>
    <ul class="hu-list">${cards}</ul>
  </section>`;
}

function todayBody(m: TodayModel, ctx: SeeContext): string {
  const head = digestHeader(ctx);
  const heads = headsUpStrip(ctx.alerts);
  const inbox = ctx.inbound
    ? `<button type="button" class="inbound-banner" data-act="open-inbound">${icon("event", 18)}<span>${ctx.inbound} ${ctx.inbound === 1 ? "email needs" : "emails need"} a look</span><span class="ib-caret" aria-hidden="true">${icon("caret-right", 16)}</span></button>`
    : "";
  const hasPlan = m.todayItems.length || m.slipped.length || m.nextDays.some((d) => d.items.length);
  if (!ctx.inbound && !heads && !hasPlan) return `${head}${emptyToday(ctx)}`;
  // Heads-ups / inbound but an otherwise-clear plan: show them above a light "nothing on today" line.
  if (!hasPlan) return `${head}${heads}${inbox}<p class="subtle">Nothing on today's plan.</p>`;
  const n = m.todayItems.length;
  const tmrw = tomorrowItems(m);
  const tmrwRows = tmrw.length
    ? tmrw.map((it) => `<div class="prow"><button type="button" class="peek-open" data-act="open" data-id="${escapeHtml(it.id)}">${escapeHtml(it.title)}${it.type === "event" ? sourceBadge(it) : ""}</button>${rowOwner(it, ctx)}</div>`).join("")
    : `<div class="prow"><span style="color:var(--color-text-muted)">Nothing tomorrow.</span></div>`;
  return `${head}${heads}${inbox}<p class="subtle">${n} thing${n !== 1 ? "s" : ""} today${ctx.filter === "mine" ? " · just yours" : ""}</p>
    ${panel(m.todayItems.map((it) => rowItem(it, ctx)).join(""))}
    ${slippedLineHTML(m.slipped)}
    <div class="section-h">Tomorrow</div>
    <div class="peek">${tmrwRows}</div>
    <button type="button" class="linklike see-week" data-act="open-week-view">${iconLabel("caret-right", "This week")}</button>`;
}

/** The full digest sheet (REQ-DIGEST-001) — Needs-a-look, Today, Slipped/Stale, and Coming up. */
export function digestSheetHTML(d: DigestModel, ctx: SeeContext): string {
  const sect = (title: string, inner: string) => (inner ? `<div class="section-h">${title}</div>${inner}` : "");
  const needs = d.needsALook
    ? `<button type="button" class="inbound-banner" data-act="open-inbound">${icon("event", 18)}<span>${d.needsALook} to review from your inbox</span><span class="ib-caret" aria-hidden="true">${icon("caret-right", 16)}</span></button>`
    : "";
  const today = d.todayItems.length ? panel(d.todayItems.map((it) => rowItem(it, ctx)).join("")) : `<p class="dg-none">Nothing on today — enjoy the calm.</p>`;
  const staleNote = d.stale.length ? `<p class="dg-note">${d.stale.length} ${d.stale.length === 1 ? "task has" : "tasks have"} been slipping a while.</p>` : "";
  const slipped = d.slipped.length ? staleNote + panel(d.slipped.map((it) => rowItem(it, ctx)).join("")) : "";
  const dayBlocks = d.upcoming
    .filter((g) => g.items.length)
    .map((g) => `<div class="dg-day"><div class="day-head"><span>${escapeHtml(g.label)}</span> <span class="day-num">${escapeHtml(g.date.slice(8))}</span></div>${panel(g.items.map((it) => rowItem(it, ctx)).join(""))}</div>`)
    .join("");
  const body = `<div class="shbody">
      ${needs}
      ${sect("Today", today)}
      ${sect("Slipped", slipped)}
      ${sect(d.period === "weekly" ? "This week" : "Coming up", dayBlocks)}
    </div>
    <div class="sheetfoot"><button type="button" class="btn" data-act="capture-close">Close</button></div>`;
  return sheetShell("digest-h", digestGreeting(d.period), "capture-close", body);
}

// ── Week: List agenda (mock .wk) / Board (mock .board) ──
function weekSeg(ctx: SeeContext): string {
  const layout = ctx.weekLayout ?? "list";
  const btn = (v: WeekLayout, label: string): string =>
    `<button type="button" class="seg-btn" aria-pressed="${layout === v}" data-week-layout="${v}">${label}</button>`;
  return `<div class="toolbar"><div class="seg" role="group" aria-label="Week layout">${btn("list", "List")}${btn("board", "Board")}</div></div>`;
}
function wchip(it: ViewItem, ctx: SeeContext): string {
  const time = it.type === "event" && it.due_at ? timeOfDay(it.due_at) : "";
  const lead = time ? `<span class="lc">${escapeHtml(time)}</span>` : `<span class="mk" aria-hidden="true"></span>`;
  const rec = it.rrule ? `<span class="chip-recur" aria-label="repeats">${icon("repeat", 12)}</span>` : "";
  return `<button type="button" class="wchip" data-act="open" data-id="${escapeHtml(it.id)}">${lead}<span class="wchip-title">${escapeHtml(it.title)}${it.type === "event" ? sourceBadge(it) : ""}</span>${rec}${rowOwner(it, ctx)}</button>`;
}
function weekTermLine(mondayDate: string, ctx: SeeContext): string {
  const woy = isoWeek(mondayDate);
  const tc = termContext(mondayDate, ctx.termCtx);
  return `<p class="subtle" style="margin-top:2px">Week ${woy} of ${mondayDate.slice(0, 4)}${tc ? ` · Term ${tc.term}, week ${tc.week}` : ""}</p>`;
}
function weekListBody(week: WeekModel, ctx: SeeContext): string {
  const days = week.days.map((d) => {
    const isToday = d.date === week.today;
    const body = d.items.length ? d.items.map((it) => wchip(it, ctx)).join("") : `<p class="day-empty">—</p>`;
    return `<section class="day${isToday ? " today" : ""}" aria-label="${escapeHtml(isToday ? "Today" : d.label)} ${escapeHtml(d.date.slice(8))}"><div class="day-head"><span>${escapeHtml(isToday ? "Today" : d.label)}</span> <span class="day-num">${escapeHtml(d.date.slice(8))}</span></div>${body}</section>`;
  }).join("");
  return weekTermLine(week.days[0]?.date ?? week.today, ctx) + `<div class="week">${days}</div>`;
}
function boardCard(it: ViewItem, ctx: SeeContext): string {
  const isEvent = it.type === "event";
  const time = isEvent && it.due_at ? timeOfDay(it.due_at) : "";
  const lead = isEvent
    ? (time ? `<span class="bt">${escapeHtml(time)}</span>` : "")
    : `<span class="bk" aria-hidden="true">${icon("task", 14)}</span>`;
  const rec = it.rrule ? `<span class="chip-recur" aria-label="repeats">${icon("repeat", 12)}</span>` : "";
  return `<button type="button" class="bitem ${isEvent ? "event" : "task"}" data-act="open" data-id="${escapeHtml(it.id)}">${lead}<span class="btitle">${escapeHtml(it.title)}</span>${rec}${rowOwner(it, ctx)}</button>`;
}
function weekBoardBody(week: WeekModel, ctx: SeeContext): string {
  const cols = week.days.map((d) => {
    const isToday = d.date === week.today;
    const cards = d.items.map((it) => boardCard(it, ctx)).join("") || `<p class="bnone">—</p>`;
    return `<div class="bcol${isToday ? " today" : ""}"><div class="bch"><span>${escapeHtml(isToday ? "Today" : d.label)}</span><span>${escapeHtml(d.date.slice(8))}</span></div>${cards}</div>`;
  }).join("");
  return weekTermLine(week.days[0]?.date ?? week.today, ctx) + `<div class="board">${cols}</div>`;
}
function weekBody(week: WeekModel, ctx: SeeContext): string {
  const empty = week.days.every((d) => d.items.length === 0);
  const body = empty
    ? `<div class="calm-empty">${icon("sun", 40)}<p>A clear week — enjoy the calm.</p></div>`
    : (ctx.weekLayout ?? "list") === "board" ? weekBoardBody(week, ctx) : weekListBody(week, ctx);
  return weekSeg(ctx) + body;
}

// ── Month (mock monthView) — count-only cells, today filled; single-letter day heads; Wk column. ──
const DOW = ["M", "T", "W", "T", "F", "S", "S"];
// A month cell (mockup-v9): a centred day number, an accent count when the day has items, and a
// filled circle on today. Count-only at every width — the mock never puts event names in the grid.
function monthCell(cell: { date: string; day: number; inMonth: boolean; items: ViewItem[] }, todayDate?: string): string {
  const n = cell.items.length;
  const isToday = !!todayDate && cell.date === todayDate;
  const label = `${escapeHtml(cell.date)}${n ? `, ${n} item${n > 1 ? "s" : ""}` : ""}${isToday ? ", today" : ""}`;
  return `<div class="month-cell${cell.inMonth ? "" : " out"}${isToday ? " today" : ""}" role="gridcell">
    <button type="button" class="cell-open" data-act="open-day" data-date="${escapeHtml(cell.date)}" aria-label="${label}"><span class="cell-day">${cell.day}</span>${n ? `<span class="cell-dots" aria-hidden="true">${'<span class="cell-dot"></span>'.repeat(Math.min(n, 3))}</span>` : ""}</button>
  </div>`;
}
function weekColumn(mondayDate: string, termCtx?: Term[] | null): string {
  const woy = isoWeek(mondayDate);
  const tc = termContext(mondayDate, termCtx);
  const term = tc ? `<span class="wk-term">T${tc.term}·${tc.week}</span>` : "";
  return `<span class="week-col" role="rowheader"><button type="button" class="week-col-btn" data-act="open-week" data-date="${escapeHtml(mondayDate)}" aria-label="Week ${woy}${tc ? `, Term ${tc.term} week ${tc.week}` : ""}"><span class="wk-num">${woy}</span>${term}</button></span>`;
}
function monthBody(m: MonthModel, termCtx?: Term[] | null, todayDate?: string): string {
  return `<section class="month" aria-label="${escapeHtml(m.label)}">
    <div class="month-grid" role="grid" aria-label="${escapeHtml(m.label)}">
      <div class="month-dow" role="row"><span class="dow wk-head" role="columnheader">Wk</span>${DOW.map((d) => `<span class="dow" role="columnheader">${d}</span>`).join("")}</div>
      ${m.weeks.map((week) => `<div class="month-week" role="row">${weekColumn(week[0].date, termCtx)}${week.map((c) => monthCell(c, todayDate)).join("")}</div>`).join("")}
    </div>
  </section>`;
}

function dayBody(day: DayGroup | undefined, ctx: SeeContext): string {
  if (!day) return placeholder("Calendar");
  if (!day.items.length) return `<div class="calm-empty">${icon("calendar", 40)}<p>Nothing on ${escapeHtml(day.label)} — a clear day.</p></div>`;
  return panel(day.items.map((it) => rowItem(it, ctx)).join(""));
}

// ── Tasks (mock tasks()) — Overdue / Today / Later / Anytime sections ──
function taskSection(title: string, items: ViewItem[], ctx: SeeContext): string {
  if (!items.length) return "";
  return `<section class="task-sec"><h2>${title}</h2>${panel(items.map((it) => rowItem(it, ctx)).join(""))}</section>`;
}
function tasksBody(m: ShellModels, ctx: SeeContext): string {
  const t = m.tasks;
  if (!t) return placeholder("Tasks");
  // A recently-ticked-off affordance (mockup-v9 undo pattern) so a task done by accident can be
  // restored — the peer of the Trolley's Undo. Only shown when something has been completed.
  const undo = ctx.tasksDone
    ? `<div class="toolbar"><button type="button" class="link" data-act="open-task-undo" style="margin-left:auto">↩ Undo (${ctx.tasksDone})</button></div>`
    : "";
  const secs = taskSection("Overdue", t.overdue, ctx) + taskSection("Today", t.today, ctx) + taskSection("Later", t.later, ctx) + taskSection("Anytime", t.anytime, ctx);
  return undo + (secs || `<p class="subtle" style="margin-top:12px">No tasks — nice.</p>`);
}

// ── Trolley (mock trolley()) — Supermarket / Shops, quantity, tick-circle on the right, Undo. ──
const STORES: Array<[string, string]> = [["supermarket", "Supermarket"], ["shops", "Shops"]];
function splitQty(title: string): { qty: string; name: string } {
  const m = title.match(/^(\d+)\s*[x×]\s+(.*)$/i);
  return m ? { qty: m[1], name: m[2] } : { qty: "", name: title };
}
function trolleyRow(it: ViewItem): string {
  const id = escapeHtml(it.id);
  const { qty, name } = splitQty(it.title);
  return `<li class="item">
    <span class="qty">${qty ? escapeHtml(qty) + " ×" : ""}</span>
    <button type="button" class="row-open" data-act="open-grocery" data-id="${id}"><span class="it-title">${escapeHtml(name)}</span></button>
    <button type="button" class="tickbtn" data-act="done" data-id="${id}" aria-label="Got ${escapeHtml(name)}"><span class="circle" aria-hidden="true"></span></button>
  </li>`;
}
function trolleyBody(groceries: ViewItem[], ctx: SeeContext): string {
  const tabs = STORES.map(([k, label]) => `<button type="button" class="seg-btn" aria-pressed="${ctx.trolleyStore === k}" data-store="${k}">${label}</button>`).join("");
  const undo = ctx.trolleyDone ? `<button type="button" class="link" data-act="open-undo" style="margin-left:auto">↩ Undo (${ctx.trolleyDone})</button>` : "";
  const list = ctx.trolleyStore === "supermarket" ? groceries : [];
  const body = list.length
    ? `<ul class="panel items">${list.map(trolleyRow).join("")}</ul>`
    : `<div class="calm-empty">${icon("grocery", 40)}<p>Nothing on this list.</p></div>`;
  return `<section class="trolley" aria-label="Trolley">
    <div class="toolbar"><div class="seg" role="group" aria-label="Choose a store">${tabs}</div>${undo}</div>
    <form class="trolley-add" aria-label="Add to Trolley">
      <input class="input" name="grocery" type="text" placeholder="Add an item…" autocomplete="off" enterkeyhint="done" aria-label="Add an item to the Trolley" />
      <select class="input trolley-qty" name="qty" aria-label="Quantity">${[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => `<option value="${n}">${n}</option>`).join("")}</select>
      <button type="submit" class="btn primary">Add</button>
    </form>
    ${body}
  </section>`;
}

function placeholder(label: string): string {
  return `<div class="calm-empty">${icon("calendar", 40)}<p>${escapeHtml(label)} is coming together.</p></div>`;
}

// ── Chrome: rail / top bar / bottom nav / FAB / avatar sheet ──
const NAV_ICON: Record<Tab, IconName> = { today: "calendar", calendar: "calendar-dots", tasks: "task", trolley: "grocery" };
const NAV_LABEL: Record<Tab, string> = { today: "Today", calendar: "Calendar", tasks: "Tasks", trolley: "Trolley" };

function avatarInitials(ctx: SeeContext): { text: string; color: string } {
  return { text: initials(ctx.me.name), color: ctx.ownerColor?.(ctx.me.id) ?? "var(--color-owner-1)" };
}
function railNav(ctx: SeeContext): string {
  const av = avatarInitials(ctx);
  const link = (t: Tab): string =>
    `<button type="button" class="rail-item" aria-current="${ctx.activeTab === t ? "page" : "false"}" data-tab="${t}">${icon(NAV_ICON[t])}<span class="rail-label">${NAV_LABEL[t]}</span></button>`;
  return `<aside class="rail" aria-label="Views">
    <div class="rail-brand">${icon("calendar")}<span>${escapeHtml(ctx.familyName)}</span></div>
    ${link("today")}${link("calendar")}${link("tasks")}${link("trolley")}
    <button type="button" class="rail-add" data-act="capture">${iconLabel("plus", "Add")}</button>
    <button type="button" class="rail-foot" data-act="avatar" aria-haspopup="menu" aria-expanded="${ctx.avatarOpen}" aria-label="Menu for ${escapeHtml(ctx.me.name)}">
      <span class="avatar-btn" aria-hidden="true" style="background:${av.color}">${escapeHtml(av.text)}</span><span class="rail-label">${escapeHtml(ctx.me.name)}</span>
    </button>
  </aside>`;
}
function topBar(ctx: SeeContext): string {
  const av = avatarInitials(ctx);
  const dated = ctx.activeTab === "today" || ctx.activeTab === "calendar";
  const sub = ctx.dateSub ? `<span class="date-sub">${escapeHtml(ctx.dateSub)}</span>` : "";
  const dateControl = `<button type="button" data-act="open-month" aria-label="Open month, ${escapeHtml(ctx.dateLabel)}" class="topbar-date"><span class="td-label">${escapeHtml(ctx.dateLabel)}${sub}</span>${icon("caret-down", 16)}</button>`;
  const nav = dated
    ? `<div class="topbar-nav"><button type="button" class="tool-btn" data-act="prev" aria-label="Previous">${icon("caret-left")}</button><h1 class="topbar-h">${dateControl}</h1><button type="button" class="tool-btn" data-act="next" aria-label="Next">${icon("caret-right")}</button></div>`
    : `<h1 class="topbar-h"><span class="topbar-date">${escapeHtml(ctx.dateLabel)}</span></h1>`;
  const scope = ctx.activeTab !== "trolley"
    ? `<button type="button" class="scope" data-act="scope-toggle" aria-label="Viewing ${ctx.filter === "all" ? "everyone" : "just me"} — tap to switch">Viewing <b>${ctx.filter === "all" ? "Everyone" : "Just me"}</b></button>`
    : "";
  return `<header class="topbar">${nav}<div class="topbar-actions">${scope}<button type="button" class="avatar-btn" data-act="avatar" aria-haspopup="menu" aria-expanded="${ctx.avatarOpen}" aria-label="Menu for ${escapeHtml(ctx.me.name)}" style="background:${av.color}">${escapeHtml(av.text)}</button></div></header>`;
}
function bottomNav(ctx: SeeContext): string {
  const tab = (t: Tab): string => {
    const badge = t === "today" && ctx.todayCount ? `<span class="nbadge">${ctx.todayCount}</span>` : "";
    return `<button type="button" class="nav-item" aria-current="${ctx.activeTab === t ? "page" : "false"}" data-tab="${t}">${icon(NAV_ICON[t])}<span class="nav-label">${NAV_LABEL[t]}</span>${badge}</button>`;
  };
  return `<nav class="bottom-nav" aria-label="Views">${tab("today")}${tab("calendar")}${tab("tasks")}${tab("trolley")}</nav>`;
}
function addFab(): string {
  return `<button type="button" class="fab" data-act="capture">${iconLabel("plus", "Add")}</button>`;
}
function avatarMenu(ctx: SeeContext): string {
  return `<div class="menu-backdrop" data-act="avatar-close"></div>
  <div class="avatar-menu" role="menu" aria-label="${escapeHtml(ctx.me.name)}">
    <p class="menu-name">${escapeHtml(ctx.me.name)}</p>
    <button type="button" role="menuitem" class="menu-row" data-act="settings">${icon("palette")}<span><span class="menu-title">Make it mine</span><span class="menu-sub">Theme, colour</span></span></button>
    <button type="button" role="menuitem" class="menu-row" data-act="account">${icon("person")}<span><span class="menu-title">Account</span><span class="menu-sub">Mailbox, feedback</span></span></button>
    <button type="button" role="menuitem" class="menu-row" data-act="theme">${icon(ctx.theme === "dark" ? "sun" : "moon")}<span>${ctx.theme === "dark" ? "Day mode" : "Night mode"}</span></button>
    <button type="button" role="menuitem" class="menu-row danger" data-act="signout">${icon("sign-out")}<span>Sign out</span></button>
  </div>`;
}

function calBody(m: ShellModels, ctx: SeeContext): string {
  switch (ctx.calMode ?? "month") {
    case "week": return weekBody(m.week, ctx);
    case "day": return dayBody(m.day, ctx);
    default: return m.month ? monthBody(m.month, ctx.termCtx, ctx.todayDate) : placeholder("Calendar");
  }
}
function tabBody(m: ShellModels, ctx: SeeContext): string {
  switch (ctx.activeTab) {
    case "today": return todayBody(m.today, ctx);
    case "calendar": return calBody(m, ctx);
    case "tasks": return tasksBody(m, ctx);
    case "trolley": return trolleyBody(m.trolley ?? [], ctx);
  }
}

/** The full app shell: desktop rail + main column (top bar + active tab) + phone bottom nav + Add
 *  FAB (+ avatar sheet). CSS picks the rail or the bottom nav by width (mock @860). */
export function renderShell(m: ShellModels, ctx: SeeContext): string {
  return (
    railNav(ctx) +
    `<div class="main-col">` +
    topBar(ctx) +
    `<div class="see" id="see-main">${tabBody(m, ctx)}</div>` +
    addFab() +
    bottomNav(ctx) +
    `</div>` +
    (ctx.avatarOpen ? avatarMenu(ctx) : "")
  );
}
